import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../LanguageContext';
import { useAuth } from '../AuthContext';
import { staleLeads, patchLeadStatus, transitionLeadStatus } from '../api';
import { LeadStory } from '../components/LeadStory';

const STATUSES = ['new', 'contacted', 'interested', 'site_visit_scheduled', 'site_visit_done', 'offer_sent', 'negotiating', 'won', 'lost'];
const STATUS_COLORS = { new: '#6366f1', contacted: '#3b82f6', interested: '#8b5cf6', site_visit_scheduled: '#f59e0b', site_visit_done: '#f97316', offer_sent: '#06b6d4', negotiating: '#ec4899', won: '#22c55e', lost: '#ef4444' };
const PAGE_SIZE = 30;

// Admin/manager page: stagnant leads (no upcoming action). Update status inline or
// schedule a follow-up to revive them; directors can filter by employee.
export default function StaleLeads() {
  const { lang, isRTL, t: tr } = useLanguage();
  const { user } = useAuth();
  const ar = lang === 'ar';
  const t = (a, e) => (ar ? a : e);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [member, setMember] = useState('');
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(null); // lead id being mutated
  const [expandedId, setExpandedId] = useState(null); // lead whose full story is open

  const load = useCallback(async () => {
    setLoading(true); setFailed(false);
    setExpandedId(null);
    const res = await staleLeads({ member, page, limit: PAGE_SIZE });
    if (res.error) { setData(null); setFailed(true); } else { setData(res); }
    setLoading(false);
  }, [member, page]);
  useEffect(() => { load(); }, [load]);

  const toggleHistory = (l) => setExpandedId(id => (id === l.id ? null : l.id));

  const statusLabel = (s) => {
    const m = { new: ['جديد', 'New'], contacted: ['تم التواصل', 'Contacted'], interested: ['مهتم', 'Interested'], site_visit_scheduled: ['زيارة مجدولة', 'Visit scheduled'], site_visit_done: ['تمت الزيارة', 'Visit done'], offer_sent: ['عرض مُرسل', 'Offer sent'], negotiating: ['تفاوض', 'Negotiating'], won: ['مكتسب', 'Won'], lost: ['خسارة', 'Lost'] };
    return m[s] ? t(m[s][0], m[s][1]) : s;
  };
  const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');
  const daysAgo = (d) => {
    if (!d) return null;
    return Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  };
  const reason = (l) => {
    if (!l.followUpDate && !l.siteVisitDate) return t('لا يوجد إجراء مجدول', 'No action scheduled');
    return t('الإجراء المجدول فات', 'Scheduled action passed');
  };

  const changeStatus = async (l, next) => {
    if (next === l.status) return;
    setBusy(l.id);
    const res = await patchLeadStatus(l.id, next);
    if (!res.error) await load(); else setBusy(null);
  };
  const scheduleFollowUp = async (l, dateStr) => {
    if (!dateStr) return;
    setBusy(l.id);
    const res = await transitionLeadStatus(l.id, {
      toStatus: l.status, followUpDate: dateStr, outcome: 'reschedule',
      statusNote: t('جدولة متابعة من صفحة العملاء الراكدين', 'Follow-up scheduled from stale leads'),
      doneBy: user?.name || '',
    });
    if (!res.error) await load(); else { setBusy(null); alert(res.error || t('تعذّر الحفظ', 'Could not save')); }
  };

  const total = data?.total || 0;
  const leads = data?.leads || [];
  const members = data?.members || [];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const sel = { padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.82rem', cursor: 'pointer' };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 1000, margin: '0 auto', padding: '4px 4px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ fontSize: '1.15rem' }}>⚠️</span>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0, color: '#dc2626' }}>{t('العملاء الراكدون', 'Stale leads')}</h2>
        {!loading && <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#fff', background: '#dc2626', borderRadius: 10, padding: '2px 10px' }}>{total}</span>}
      </div>
      <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
        {t('عملاء لم تُجدوَل لهم أي متابعة أو زيارة قادمة — بحاجة إلى إجراء. حدّث الحالة أو جدول متابعة لتنشيطهم.',
          'Leads with no upcoming follow-up or visit scheduled — they need action. Update the status or schedule a follow-up to revive them.')}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={member} onChange={e => { setMember(e.target.value); setPage(0); }} style={sel}>
          <option value="">{t('كل الموظفين', 'All employees')}</option>
          {members.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>...</div>
      ) : failed ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          {t('تعذّر تحميل البيانات', 'Could not load data')}
          <div style={{ marginTop: 12 }}><button onClick={load} style={sel}>{t('إعادة المحاولة', 'Retry')}</button></div>
        </div>
      ) : leads.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('لا يوجد عملاء راكدون 🎉', 'No stale leads 🎉')}</div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {leads.map(l => {
              const sc = STATUS_COLORS[l.status] || '#888';
              const idle = daysAgo(l.lastActivity);
              const open = expandedId === l.id;
              return (
                <div key={l.id} style={{ border: '1px solid var(--border)', borderInlineStart: `3px solid ${sc}`, borderRadius: 12, background: 'var(--bg-secondary)', overflow: 'hidden', opacity: busy === l.id ? 0.5 : 1 }}>
                  {/* Clean clickable row — same presentation as the analytics member page */}
                  <div onClick={() => toggleHistory(l)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer', flexWrap: 'wrap', background: open ? 'var(--bg-primary)' : 'transparent' }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: sc, flexShrink: 0 }} />
                    <b style={{ fontSize: '0.9rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name || '—'}</b>
                    <span style={{ padding: '1px 8px', borderRadius: 7, fontSize: '0.62rem', fontWeight: 700, background: sc + '18', color: sc, whiteSpace: 'nowrap' }}>{statusLabel(l.status)}</span>
                    <a href={`tel:${l.phone}`} onClick={e => e.stopPropagation()} style={{ direction: 'ltr', fontSize: '0.76rem', color: 'var(--text-secondary)', textDecoration: 'none' }}>{l.phone}</a>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>👤 {l.assignedTo || '—'}</span>
                    {l.project && <span style={{ fontSize: '0.7rem', color: 'var(--accent)' }}>🏢 {l.project}</span>}
                    <span style={{ marginInlineStart: 'auto', fontSize: '0.68rem', fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' }}>{reason(l)}{idle != null ? ` · ${t('آخر نشاط', 'idle')} ${idle}${t('ي', 'd')}` : ''}</span>
                    <span style={{ color: sc, fontSize: '0.72rem', flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
                  </div>

                  {open && (
                    <>
                      {/* Act on the lead after reviewing its story */}
                      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid var(--border)', background: 'var(--bg-primary)' }}>
                        <label style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>{t('الحالة', 'Status')}</label>
                        <select value={l.status} onChange={e => changeStatus(l, e.target.value)} style={{ ...sel, fontSize: '0.76rem', padding: '5px 8px' }}>
                          {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
                        </select>
                        <label style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginInlineStart: 8 }}>{t('جدولة متابعة', 'Schedule follow-up')}</label>
                        <input type="datetime-local" onChange={e => scheduleFollowUp(l, e.target.value)} style={{ ...sel, fontSize: '0.76rem', padding: '5px 8px' }} />
                      </div>
                      <LeadStory leadId={l.id} t={tr} />
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16 }}>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ ...sel, opacity: page === 0 ? 0.4 : 1 }}>{t('‹ السابق', '‹ Prev')}</button>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{t(`صفحة ${page + 1} من ${totalPages}`, `Page ${page + 1} of ${totalPages}`)}</span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ ...sel, opacity: page >= totalPages - 1 ? 0.4 : 1 }}>{t('التالي ›', 'Next ›')}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
